function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useRef, forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import { useCustomField, CustomFieldContext } from '../../../common/hooks/useCustomField';
import browserAgentIs from '../../../common/utils/browserAgent';
import Option from './Option';
import Graph from './Graph';
import './index.scss';
export * from './Graph';
//@ts-ignore
const SelectDropdown = /*#__PURE__*/forwardRef(({
  name,
  value,
  placeholder,
  disabled,
  isValid,
  isInvalid,
  isAutoComplete,
  multiple,
  onChange,
  options,
  hiddenOptions,
  prependToggle,
  appendToggle,
  closeEverySelect,
  showCustomOptionLabel,
  noRecordContent,
  clearable,
  loading,
  showCaret,
  nullableValue,
  disabledOptionValues,
  filterOptions,
  className,
  onBlur,
  onFocus,
  appendChildren,
  children
}, ref) => {
  const [{
    show,
    search,
    navigation
  }, setStates] = useState({
    search: "",
    show: false,
    navigation: -1
  });
  const provide = useCustomField({
    name,
    value: multiple ? Array.isArray(value) ? value : [] : nullableValue ? value : value || "",
    onChange,
    onResetStates: () => {
      observables.notInOptionLabel = "";
    }
  });
  const [customSelect, setCustomSelect] = provide;
  const observables = useRef({
    target: null,
    optionsContainer: null,
    preventBlur: false,
    preventFocus: false,
    notInOptionLabel: "",
    searchTimeout: null
  }).current;
  const filteredOptions = useMemo(() => {
    let filteredBySearchOptions = search ? options.filter(each => {
      return new RegExp(`${search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}`, 'i').test(each.label);
    }) : options;

    if (filterOptions) {
      filteredBySearchOptions = filterOptions(filteredBySearchOptions);
    }

    return filteredBySearchOptions;
  }, [search, options, filterOptions]);
  const toggleHandlers = {
    onMouseDown: () => {
      if (show) {
        observables.preventFocus = true;
      }
    },
    onMouseUp: () => {
      if (!observables.preventFocus) {
        observables.target?.focus();
      } else {
        observables.preventFocus = false;
      }
    }
  };

  const handleFocus = event => {
    if (onFocus) {
      onFocus(event);
    }

    setStates(prev => ({ ...prev,
      show: true
    }));
  };

  const handleBlur = event => {
    if (observables.preventBlur) {
      if (browserAgentIs.firefox) {
        setTimeout(() => observables.target?.focus());
      } else {
        observables.target?.focus();
      }
    } else {
      if (onBlur) {
        onBlur(event);
      }

      observables.target.value = "";
      setStates(prev => ({ ...prev,
        show: false,
        search: "",
        navigation: -1
      }));
    }
  };

  const handleSearch = event => {
    if (observables.searchTimeout) {
      clearTimeout(observables.searchTimeout);
    }

    observables.searchTimeout = setTimeout(() => {
      setStates(prev => ({ ...prev,
        search: event.target.value
      }));
    }, 300);
  };

  const handleNavigation = event => {
    const isArrowUp = 38;
    const isArrowDown = 40;
    const isEnter = 13;
    const isESC = 27;
    const isBackSpace = event.which === 8;
    const hasNavigation = navigation !== -1;
    const optionsContainer = observables.optionsContainer;
    const optionsContainerPaddingTopAndBottomCombine = 18;

    switch (event.which) {
      case isArrowUp:
      case isArrowDown:
        event.preventDefault();
        const navigations = filteredOptions.length - 1;
        const currentNavigation = hasNavigation ? event.which === isArrowDown ? Math.min(navigation + 1, navigations) : Math.max(navigations < navigation ? navigations : navigation - 1, 0) : 0;
        const optHeight = optionsContainer.firstElementChild?.offsetHeight || 0;
        const navigator_pos = optHeight * currentNavigation;
        const scrollTop = event.which === isArrowDown ? navigator_pos - (optionsContainer.offsetHeight - optionsContainerPaddingTopAndBottomCombine - optHeight) : navigator_pos;
        if (event.which === isArrowDown ? optionsContainer.scrollTop < scrollTop : optionsContainer.scrollTop > scrollTop) optionsContainer.scrollTop = scrollTop;
        setStates(prev => ({ ...prev,
          navigation: currentNavigation
        }));
        if (!multiple) observables.target.value = filteredOptions[currentNavigation].label;
        break;

      case isEnter:
        event.preventDefault();
        setCustomSelect(prev => {
          let theValue = filteredOptions[navigation === -1 ? 0 : navigation]?.value || "";

          if (multiple) {
            if (selected) {
              theValue = prev.value.filter(item => item !== value);
            } else {
              theValue = Array.isArray(prev.value) ? [...prev.value, value] : [value];
            }
          }

          return { ...prev,
            value: theValue,
            dirty: true
          };
        });
        optionsContainer.scrollTop = 0;

        if (!multiple) {
          observables.target?.blur();
        }

        break;

      case isESC:
        event.stopPropagation();
        observables.target?.blur();
        break;

      default:
        break;
    }
  };

  const handleClear = event => {
    event.stopPropagation();
    setCustomSelect(prev => ({ ...prev,
      value: "",
      dirty: true
    }));

    if (showCustomOptionLabel) {
      observables.notInOptionLabel = "";
    }
  };

  const optionContainerHandlers = {
    // one step faster than onBlur so it can stop the change when clicking 
    onMouseDown: () => {
      observables.preventBlur = true;
      observables.notInOptionLabel = "";
    },
    onMouseLeave: () => {
      // when mouse down and leave the options to fixed bug not bluring
      if (observables.preventBlur) {
        observables.preventBlur = false;
      }
    },
    // core selecting a option
    onMouseUp: event => {
      observables.preventBlur = false;
      const isOption = event.target.closest('.select-option');
      const isSelected = event.target.closest('.select-option.selected');
      const isNotSelected = event.target.closest('.select-option.unclosable');

      if (showCustomOptionLabel && isOption) {
        observables.notInOptionLabel = isOption.textContent;
      } // if(isNotSelected || isSelected) return;


      if (isNotSelected) return;

      if (!multiple && isOption || closeEverySelect) {
        observables.target?.blur();
      }
    }
  };
  const selected = useMemo(() => {
    if (multiple) {
      return [];
    } else {
      const findValueInOption = (hiddenOptions ? [...options, ...hiddenOptions] : options).find(option => option.value == customSelect.value);
      return findValueInOption?.labelNode || findValueInOption?.label || observables.notInOptionLabel;
    }
  }, [customSelect.value, options]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames('select-dropdown', {
      show: isAutoComplete ? show && !!search : show,
      multiple,
      disabled,
      'is-valid': isValid,
      'is-invalid': isInvalid
    })
  }, /*#__PURE__*/React.createElement("input", {
    ref: target => {
      observables.target = target;

      if (ref) {
        ref.current = target;
      }
    },
    className: "select-toggle" // value={customSelect.value}
    ,
    autoComplete: "new-password" // disabled the auto complete popup
    ,
    disabled: disabled,
    onKeyDown: handleNavigation,
    onChange: handleSearch,
    onBlur: handleBlur,
    onFocus: handleFocus,
    placeholder: placeholder
  }), /*#__PURE__*/React.createElement("div", _extends({
    className: classNames('select-toggle', {}, className)
  }, toggleHandlers, {
    "aria-hidden": !show
  }), prependToggle, selected ? /*#__PURE__*/React.createElement("span", {
    className: "select-text"
  }, selected) : /*#__PURE__*/React.createElement("span", {
    className: "select-placeholder"
  }, loading || placeholder), loading && /*#__PURE__*/React.createElement("div", {
    className: "select-dropdown-loader"
  }, /*#__PURE__*/React.createElement("span", {
    className: "select-dropdown-loader-dot-one"
  }), /*#__PURE__*/React.createElement("span", {
    className: "select-dropdown-loader-dot-two"
  }), /*#__PURE__*/React.createElement("span", {
    className: "select-dropdown-loader-dot-three"
  })), clearable && selected && !disabled && /*#__PURE__*/React.createElement("span", {
    className: "select-clear",
    onMouseUp: handleClear
  }, /*#__PURE__*/React.createElement("svg", {
    height: "20",
    width: "20",
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    className: "css-tj5bde-Svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
  }))), showCaret && !isAutoComplete && /*#__PURE__*/React.createElement("span", {
    className: "select-angle"
  }, /*#__PURE__*/React.createElement("svg", {
    height: "20",
    width: "20",
    viewBox: "0 0 20 20",
    "aria-hidden": "true",
    focusable: "false",
    className: "css-tj5bde-Svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
  }))), appendToggle), /*#__PURE__*/React.createElement("div", _extends({
    ref: optionsContainer => observables.optionsContainer = optionsContainer,
    className: "select-options-container"
  }, optionContainerHandlers), /*#__PURE__*/React.createElement(CustomFieldContext.Provider, {
    value: provide
  }, loading ? /*#__PURE__*/React.createElement("div", {
    className: "select-option-loading"
  }, "Loading") : filteredOptions.length > 0 || children ? /*#__PURE__*/React.createElement(React.Fragment, null, !appendChildren && children, filteredOptions.map((option, key) => {
    return /*#__PURE__*/React.createElement(Option, {
      key: `select-key-${option.value}`,
      className: navigation === key ? 'navigating' : undefined,
      disabled: disabledOptionValues && disabledOptionValues.includes(option.value),
      value: option.value
    }, option.labelNode || option.label || option.value);
  }), appendChildren && children) : /*#__PURE__*/React.createElement("div", {
    className: "select-no-option"
  }, noRecordContent || /*#__PURE__*/React.createElement("div", {
    className: "select-no-option-content"
  }, /*#__PURE__*/React.createElement("i", {
    className: "fa fa-inbox empty-icon"
  }), /*#__PURE__*/React.createElement("small", null, "No option to display"))))));
});
SelectDropdown.Option = Option;
SelectDropdown.Graph = Graph;
SelectDropdown.defaultProps = {
  multiple: false,
  disabled: false,
  closeEverySelect: false,
  clearable: true,
  showCaret: true
};
export default SelectDropdown;